import React, { useCallback, useEffect, useState } from "react";
import cx from "classnames";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { usePopper } from "react-popper";
import GifPicker from "gif-picker-react";
import { useLanguage } from "@jugl-web/utils/i18n/EnhancedIntlProvider";
import { useFCM } from "@web-src/modules/notifications/providers/FCMProvider/useFCM";
import { Menu, PlainButton } from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import { AttachmentType } from "@web-src/modules/chats/types";
import { environment } from "@web-src/environments/environment";
import useChatInputState from "../../hooks/useChatInputState";
import { ReactComponent as AttachmentsIcon } from "./assets/attachments.svg";
import { ReactComponent as SmileIcon } from "./assets/smile.svg";
import { ReactComponent as GIFIcon } from "./assets/gif.svg";
import { ReactComponent as VideoMessageIcon } from "./assets/video-message.svg";
import { ReactComponent as ImageIcon } from "./assets/image.svg";
import { ReactComponent as VideoIcon } from "./assets/video.svg";
import { ReactComponent as AudioIcon } from "./assets/audio.svg";
import { ReactComponent as DocumentIcon } from "./assets/document.svg";
import { ReactComponent as TextFormattingIcon } from "./assets/text-formatting.svg";

export type ToolbarActions = {
  attachments?: {
    enabled: boolean;
    isMultiple?: boolean;
    accept?: Partial<Record<AttachmentType, string>>;
    maxSize?: Partial<Record<AttachmentType, number>>;
    isCaptionHidden?: boolean;
  };
  audioMessage?: {
    enabled: boolean;
  };
  emoji?: {
    enabled: boolean;
  };
  gif?: {
    enabled: boolean;
  };
  videoMessage?: {
    enabled: boolean;
  };
  textFormatting?: {
    enabled: boolean;
  };
};

export type ToolbarActionType = keyof ToolbarActions;
export interface ChatInputToolbarParams {
  onAction: (params: {
    attachmentType?: AttachmentType;
    type: ToolbarActionType;
    emoji?: string;
    gif?: string;
  }) => void;
  disabledNewFiles?: boolean;
  nonEditableText?: boolean;
  id: string;
  onAddAttachments?: () => void;
  toolbarActions?: ToolbarActions;
  className?: string;
}

export const ChatInputToolbar: React.FC<ChatInputToolbarParams> = ({
  onAction,
  disabledNewFiles,
  nonEditableText,
  id,
  onAddAttachments,
  toolbarActions,
  className,
}) => {
  const { t } = useTranslations();
  const { chatsState } = useChatInputState();
  const getBtnClass = useCallback(
    (type: "text" | "file") =>
      cx(
        "m-0 block h-[40px] w-[40px] rounded border-none bg-white p-0 cursor-pointer hover:bg-gray-100",
        {
          "pointer-events-none opacity-50":
            (type === "text" && nonEditableText) ||
            (type === "file" && disabledNewFiles),
        }
      ),
    [disabledNewFiles, nonEditableText]
  );

  const chatState = chatsState[id];
  const hasChatFiles = !!chatState?.attachments?.length;
  const maxAttachments = toolbarActions?.attachments?.isMultiple
    ? environment.featureFlags.maxChatAttachmentsCount
    : 1;
  const canAddMoreAttachments =
    (chatState?.attachments?.length || 0) < maxAttachments;

  const [emojiBtnRef, setEmojiBtnRef] = useState<HTMLButtonElement | null>();
  const [emojiPopoverRef, setEmojiPopoverRef] = useState<HTMLElement | null>();
  const { styles: emojiPopperStyles, attributes: emojiPopperAttrs } = usePopper(
    emojiBtnRef,
    emojiPopoverRef,
    {
      placement: "top-start",
    }
  );
  const { dateLocale } = useLanguage();
  const { logEvent } = useFCM();

  const [isTextFormattingBarOpen, setIsTextFormattingBarOpen] = useState(false);
  const [gifBtnRef, setGifBtnRef] = useState<HTMLButtonElement | null>();
  const [gifPopoverRef, setGifPopoverRef] = useState<HTMLElement | null>();
  const { styles: gifPopperStyles, attributes: gifPopperAttrs } = usePopper(
    gifBtnRef,
    gifPopoverRef,
    {
      placement: "top-start",
    }
  );

  const handleClick = useCallback(
    (type: ToolbarActionType, attachmentType: AttachmentType) => {
      onAction?.({ type, attachmentType });
      if (type === "attachments") {
        logEvent("action_chat_attachment");
      }
    },
    [logEvent, onAction]
  );

  useEffect(() => {
    setIsTextFormattingBarOpen(false);
  }, [id]);

  return (
    <div className={cx("flex gap-[10px]", className)}>
      {toolbarActions?.emoji?.enabled && (
        <Popover>
          <PopoverButton ref={setEmojiBtnRef} className={getBtnClass("text")}>
            <SmileIcon />
          </PopoverButton>
          <PopoverPanel
            className="z-50"
            ref={setEmojiPopoverRef}
            style={emojiPopperStyles.popper}
            {...emojiPopperAttrs.popper}
          >
            {({ close }) => (
              <Picker
                theme="light"
                locale={dateLocale?.code}
                data={data}
                skinTonePosition="none"
                previewPosition="none"
                onEmojiSelect={(emoji: { [key: string]: string }) => {
                  onAction?.({ type: "emoji", emoji: emoji.native });
                  close();
                }}
              />
            )}
          </PopoverPanel>
        </Popover>
      )}
      {toolbarActions?.gif?.enabled && !hasChatFiles && (
        <Popover>
          <PopoverButton ref={setGifBtnRef} className={getBtnClass("file")}>
            <GIFIcon />
          </PopoverButton>
          <PopoverPanel
            className="z-50"
            ref={setGifPopoverRef}
            style={gifPopperStyles.popper}
            {...gifPopperAttrs.popper}
          >
            {({ close }) => (
              <GifPicker
                tenorApiKey="AIzaSyD3gXEUnVD2OlXH6oWDjCuprKFRNCo_m1E"
                onGifClick={(e) => {
                  onAction?.({ type: "gif", gif: e.url });
                  close();
                }}
              />
            )}
          </PopoverPanel>
        </Popover>
      )}
      {toolbarActions?.attachments?.enabled &&
        toolbarActions.attachments.accept &&
        Object.keys(toolbarActions.attachments.accept).length > 0 &&
        canAddMoreAttachments && (
          <Menu
            placement="top"
            autoClose
            sections={[
              [
                {
                  id: "image",
                  label: t({ id: "chats-page.image", defaultMessage: "Image" }),
                  icon: (
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary">
                      <ImageIcon />
                    </div>
                  ),
                  isHidden: !toolbarActions.attachments.accept.image,
                  onSelect: () =>
                    handleClick("attachments", AttachmentType.image),
                },
                {
                  id: "video",
                  label: t({ id: "chats-page.video", defaultMessage: "Video" }),
                  icon: (
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-users-user2">
                      <VideoIcon />
                    </div>
                  ),
                  isHidden: !toolbarActions.attachments.accept.video,
                  onSelect: () =>
                    handleClick("attachments", AttachmentType.video),
                },
                {
                  id: "document",
                  label: t({
                    id: "chats-page.file",
                    defaultMessage: "File",
                  }),
                  icon: (
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradients-g3">
                      <DocumentIcon />
                    </div>
                  ),
                  isHidden: !toolbarActions.attachments.accept.file,
                  onSelect: () =>
                    handleClick("attachments", AttachmentType.file),
                },
                {
                  id: "audio",
                  label: t({
                    id: "chats-page.audio",
                    defaultMessage: "Audio",
                  }),
                  icon: (
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#30BEC6]">
                      <AudioIcon />
                    </div>
                  ),
                  isHidden: !toolbarActions.attachments.accept.audio,
                  onSelect: () =>
                    handleClick("attachments", AttachmentType.audio),
                },
              ],
            ]}
            renderTrigger={({ Trigger, triggerRef }) => (
              <Trigger
                as={PlainButton}
                ref={triggerRef}
                onClick={(e) => {
                  if (onAddAttachments) {
                    onAddAttachments();
                    e.preventDefault();
                  }
                }}
                className={getBtnClass("file")}
              >
                <AttachmentsIcon />
              </Trigger>
            )}
          />
        )}
      {toolbarActions?.videoMessage?.enabled && !hasChatFiles && (
        <PlainButton
          type="button"
          onClick={() => onAction({ type: "videoMessage" })}
          className={getBtnClass("file")}
        >
          <VideoMessageIcon />
        </PlainButton>
      )}
      {toolbarActions?.textFormatting?.enabled && (
        <PlainButton
          type="button"
          onClick={() => {
            onAction({ type: "textFormatting" });
            setIsTextFormattingBarOpen((prev) => !prev);
          }}
          className={cx(getBtnClass("text"), {
            "bg-grey-200": isTextFormattingBarOpen,
          })}
        >
          <TextFormattingIcon
            className={cx(
              isTextFormattingBarOpen ? "stroke-primary-800" : "stroke-dark-700"
            )}
          />
        </PlainButton>
      )}
    </div>
  );
};
